import { AxiosError } from 'axios';

/**
 * Maps API error responses to user-friendly localized error keys
 * @param error - The error from API call
 * @returns Localization key for the error message
 */
export function mapApiErrorToMessage(error: unknown): string {
  if (!error) {
    return 'auth.registration.errors.unknownError';
  }

  // Handle Axios errors (HTTP responses)
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as AxiosError;
    
    // Handle 409 Conflict errors (registration conflicts)
    if (axiosError.response?.status === 409) {
      const errorMessage = axiosError.response.data as string;
      
      if (typeof errorMessage === 'string') {
        if (errorMessage.includes('phone number is already registered')) {
          return 'auth.registration.errors.phoneAlreadyRegistered';
        }
        if (errorMessage.includes('license plate is already registered')) {
          return 'auth.registration.errors.plateAlreadyRegistered';
        }
        if (errorMessage.includes('Telegram account is already registered')) {
          return 'auth.registration.errors.telegramAlreadyRegistered';
        }
      }
      
      // Generic conflict error
      return 'auth.registration.errors.phoneAlreadyRegistered'; // Most common case
    }
    
    // Handle 400 Bad Request errors (validation errors)
    if (axiosError.response?.status === 400) {
      const errorMessage = axiosError.response.data as string;
      
      if (typeof errorMessage === 'string') {
        // Check for localization keys that come from backend validators
        // These are used for charging/extension time validation
        if (errorMessage === 'chargingTimeExceedsMaximum') {
          return 'errors.chargingTimeExceedsMaximum';
        }
        if (errorMessage === 'extensionTimeExceedsMaximum') {
          return 'errors.extensionTimeExceedsMaximum';
        }
        if (errorMessage === 'invalidChargingDuration') {
          return 'errors.invalidChargingDuration';
        }
        
        // Check for phone and license plate validation errors
        if (errorMessage.includes('phone') || errorMessage.includes('E.164')) {
          return 'auth.registration.errors.invalidPhoneFormat';
        }
        if (errorMessage.includes('license plate') || errorMessage.includes('nnn-nn-nnn')) {
          return 'auth.registration.errors.invalidPlateFormat';
        }
      }
      
      return 'errors.validation';
    }
    
    // Handle 500 Server errors
    if (axiosError.response?.status && axiosError.response.status >= 500) {
      return 'auth.registration.errors.serverError';
    }
    
    // Handle network errors
    if (axiosError.code === 'NETWORK_ERROR' || !axiosError.response) {
      return 'auth.registration.errors.networkError';
    }
  }
  
  // Handle generic Error objects
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    
    if (message.includes('charging') && message.includes('exceed')) {
      return 'errors.chargingTimeExceedsMaximum';
    }
    if (message.includes('extension') && message.includes('exceed')) {
      return 'errors.extensionTimeExceedsMaximum';
    }
    if (message.includes('phone number is already registered')) {
      return 'auth.registration.errors.phoneAlreadyRegistered';
    }
    if (message.includes('license plate is already registered')) {
      return 'auth.registration.errors.plateAlreadyRegistered';
    }
    if (message.includes('telegram account is already registered')) {
      return 'auth.registration.errors.telegramAlreadyRegistered';
    }
    if (message.includes('phone') && message.includes('format')) {
      return 'auth.registration.errors.invalidPhoneFormat';
    }
    if (message.includes('license plate') && message.includes('format')) {
      return 'auth.registration.errors.invalidPlateFormat';
    }
    if (message.includes('network')) {
      return 'auth.registration.errors.networkError';
    }
  }
  
  // Default fallback
  return 'auth.registration.errors.unknownError';
}

/**
 * Determines if an error is a registration conflict (user already exists)
 * @param error - The error from API call
 * @returns True if it's a registration conflict error
 */
export function isRegistrationConflictError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as AxiosError;
    return axiosError.response?.status === 409;
  }
  
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return message.includes('already registered');
  }
  
  return false;
}